<template>
  <rest-page
    :fetch-endpoint="fetchEndpoint"
    :class="{ 'campaign-launch-page': model.campaign.state === 'live' }"
    data-qa="game-launch-substep-page"
  >
    <section
      v-if="model.campaign.state === 'draft'"
      class="steps-preview-wrapper"
      data-qa="draft-campaign-preview-section"
    >
      <game-campaign-accordion :items="steps">
        <template v-for="step in steps" v-slot:[step.name]>
          <div
            v-if="step.name === GameCreationTabs.SET_UP"
            class="key-value-wrapper steps-preview-text"
            data-qa="setup-preview"
          >
            <div
              v-for="field in [...campaignFields, ...competitionFields]"
              :key="field.key"
              class="key-value-row"
              :data-qa="`setup-field-${field.key}`"
            >
              <span class="key">{{ field.key }}</span>
              <span>{{ field.value }}</span>
            </div>
          </div>
          <div
            v-if="step.name === GameCreationTabs.GAME_RULES"
            class="key-value-wrapper steps-preview-text"
            data-qa="game-rules-preview"
          >
            <div
              v-for="field in [...scoringFields]"
              :key="field.key"
              class="key-value-row"
              :data-qa="`game-rules-field-${field.key}`"
            >
              <span class="key">{{ field.key }}</span>
              <span>{{ field.value }}</span>
            </div>
          </div>
          <div
            v-if="step.name === GameCreationTabs.CONTENT_ASSETS"
            class="content-step-wrapper"
            data-qa="content-assets-preview"
          >
            <p class="steps-preview-text">
              {{
                $t(
                  'Gamification_GameStepPreview_ContentAssetsPreviewDescription'
                )
              }}
            </p>
            <p class="steps-preview-text">
              {{ $t('Gamification_GameStepPreview_ContentAssetsAccess') }}
              <span
                class="link"
                @click="redirectToTab(GameCreationTabs.CONTENT_ASSETS)"
                data-qa="content-assets-tab-link"
              >
                {{ $t('Gamification_ContentAssets') }}
              </span>
              {{ $t('Gamification_GameStepPreview_ContentAssetsAccessTab') }}
            </p>
          </div>
          <div
            v-if="step.name === GameCreationTabs.PRIZES"
            data-qa="prizes-preview"
          >
            <p class="steps-preview-text">
              {{ $t('Gamification_GameStepPreview_PrizesReviewDetails') }}
              <span
                class="link"
                @click="redirectToTab(GameCreationTabs.PRIZES)"
                data-qa="prizes-tab-link"
              >
                {{ $t('Gamification_Prizes') }}
              </span>
              {{ $t('Gamification_GameStepPreview_PrizesReviewDetailsTab') }}
            </p>
          </div>
        </template>
      </game-campaign-accordion>
      <el-checkbox
        v-model="isReviewed"
        class="margin-left-auto"
        data-qa="review-confirmation-checkbox"
      >
        {{ $t('Gamification_GameStepPreview_ReviewConfirmation') }}
      </el-checkbox>
      <div class="buttons-wrapper margin-left-auto" data-qa="campaign-actions">
        <el-button
          type="primary"
          plain
          :disabled="isButtonDisabled"
          @click="saveCampaignAsDraft"
          data-qa="save-draft-btn"
        >
          {{ $t('Gamification_GameStepPreview_SaveAsDraft') }}
        </el-button>
        <el-button
          type="primary"
          :disabled="!isReviewed || isButtonDisabled || requiresBracketSave"
          @click="launchCampaign"
          data-qa="launch-campaign-btn"
        >
          {{ $t('Gamification_GameStepPreview_LaunchCampaign') }}
        </el-button>
      </div>
    </section>
    <div
      v-else-if="model.campaign.state === 'live'"
      class="campaign-launch-section"
      data-qa="campaign-launched-section"
    >
      <div
        class="campaign-launch-description"
        data-qa="campaign-launch-description"
      >
        <div
          class="campaign-launch-title-wrapper"
          data-qa="campaign-launch-title"
        >
          <icon glyph="verified" class="verified-icon" />
          <h3>{{ $t('Gamification_GameStepPreview_CampaignLaunched') }}</h3>
        </div>
        <p>
          {{ $t('Gamification_GameStepPreview_CampaignLaunchedDescription1') }}
        </p>
        <p>
          {{ $t('Gamification_GameStepPreview_CampaignLaunchedDescription2') }}
        </p>
      </div>
      <div class="campaign-launch-link-wrapper">
        <p v-if="competitionName" class="title">
          {{ competitionName }}
        </p>
        <div class="link-container">
          <a
            class="link"
            target="_blank"
            rel="noopener noreferrer"
            data-qa="campaign-url-link"
          >
            {{ model.campaign.frontend_url }}
          </a>
          <el-tooltip placement="top" :content="$t('Gamification_CopyLink')">
            <icon
              glyph="clone"
              class="clone-icon"
              data-qa="copy-url-btn"
              @click="copyCampaignUrl"
            />
          </el-tooltip>
        </div>
      </div>
    </div>
  </rest-page>
</template>
<script>
import { mapGetters } from 'vuex'
import { projectRoute, projectEndpoint } from '@/core/helpers'
import RestPage from '@/common/components/rest/RestPage.vue'
import GameCampaignAccordion from '@/modules/gamification/components/games-campaigns/GameCampaignAccordion.vue'
import { GameCreationTabs } from '@/modules/gamification/enum/GameCreationStepsEnum.js'
import RestApi from '@/core/services/RestApi'
import { EventBus } from '@/core/EventBus'
import { gameScoringPointsLogicEnum } from '@/modules/gamification/enum/GameScoringPointsLogicEnum'
import { castDate } from '@/modules/gamification/helpers'

export default {
  name: 'GameLaunchSubStep',
  components: {
    RestPage,
    GameCampaignAccordion
  },
  props: {
    isCreationMode: {
      type: Boolean,
      required: true
    },
    isReadOnly: {
      type: Boolean,
      required: true
    },
    saveFlag: {
      type: Boolean,
      required: true
    },
    gameCampaignId: {
      type: Number
    }
  },
  data() {
    return {
      GameCreationTabs,
      steps: [
        {
          title: this.$t('CampaignStep_Setup'),
          name: GameCreationTabs.SET_UP
        },
        {
          title: this.$t('Gamification_GameRules'),
          name: GameCreationTabs.GAME_RULES
        },
        {
          title: this.$t('Gamification_ContentAssets'),
          name: GameCreationTabs.CONTENT_ASSETS
        },
        {
          title: this.$t('Gamification_Prizes'),
          name: GameCreationTabs.PRIZES
        }
      ],
      model: {
        campaign: {}
      },
      isReviewed: false,
      isLoading: false
    }
  },
  computed: {
    ...mapGetters(['timezones']),
    isButtonDisabled() {
      return this.isLoading || this.isReadOnly
    },
    fetchEndpoint() {
      return [
        this.fetchSetUpTabEndpoint(),
        this.fetchCompetitionConfigurationEndpoint(),
        this.fetchScoringConfiguration()
      ]
    },
    campaignFields() {
      if (!this.model || !this.model.campaign) {
        return []
      }

      const campaign = this.model.campaign

      return [
        {
          key: this.$t('Gamification_GameBasicSetupForm_Name'),
          value: campaign.name
        },
        {
          key: this.$t('Gamification_GameBasicSetupForm_StartAndEndAt'),
          value:
            campaign.starts_at && campaign.ends_at
              ? `${castDate(campaign.starts_at)} - ${castDate(
                  campaign.ends_at
                )}`
              : undefined
        },
        {
          key: this.$t('Gamification_GameBasicSetupForm_TimeZone'),
          value: this.timezones[campaign.timezone] || campaign.timezone
        },
        {
          key: this.$t('Gamification_GameBasicSetupForm_DefaultSegment'),
          value: campaign.default_segment_name
        },
        {
          key: this.$t('Gamification_GameBasicSetupForm_ExcludeSegment'),
          value: campaign.excludes_segment_name
        }
      ].filter(item => !!item.value)
    },
    competitionFields() {
      if (!this.model || !this.model.competition) {
        return []
      }

      const competition = this.model.competition

      return [
        {
          key: this.$t('Gamification_GameCompetitionTeamsForm_Competition'),
          value: competition.competition_name
        }
      ].filter(item => !!item.value)
    },
    competitionName() {
      if (!this.model || !this.model.competition) {
        return undefined
      }

      return this.model.competition.competition_name
    },
    scoringFields() {
      if (!this.model || !this.model.scoring) {
        return []
      }

      const scoring = this.model.scoring

      return [
        {
          key: this.$t('Gamification_ScoringForm_PointsLogic'),
          value: gameScoringPointsLogicEnum[scoring.points_logic]
            ? this.$t(gameScoringPointsLogicEnum[scoring.points_logic])
            : scoring.points_logic
        },
        {
          key: this.$t('Gamification_ScoringForm_TiebreakerQuestion'),
          value: scoring.tiebreaker_question
        }
      ].filter(item => !!item.value)
    },

    launchCampaignEndpoint() {
      if (this.gameCampaignId) {
        return projectEndpoint({
          method: 'post',
          url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/state`,
          data: {
            state: 'live'
          }
        })
      }
    },
    saveAsDraftEndpoint() {
      if (this.gameCampaignId) {
        return projectEndpoint({
          method: 'post',
          url: `gamify/${this.$route.params.id}/campaign/${this.gameCampaignId}/state`,
          data: {
            state: 'draft'
          }
        })
      }
    },
    requiresBracketSave() {
      const campaign = this.model && this.model.campaign

      return campaign && campaign.manual_bracket && !campaign.has_bracket
    }
  },
  watch: {
    isButtonDisabled: {
      handler(newVal) {
        this.$emit('check', newVal)
      },
      immediate: true
    },
    saveFlag: {
      async handler() {
        this.saveCampaignAsDraft()
      }
    }
  },
  methods: {
    fetchSetUpTabEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-game-campaign/${this.gameCampaignId}`,
        handler: data => {
          this.model.campaign = data['data']
        }
      })
    },
    fetchCompetitionConfigurationEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-sport-competition-configuration/${this.gameCampaignId}`,
        handler: data => {
          this.model.competition = data['data']
        }
      })
    },
    fetchScoringConfiguration() {
      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-scoring-configuration/${this.gameCampaignId}`,
        handler: data => {
          this.model.scoring = data['data']
        }
      })
    },
    redirectToTab(tab) {
      const route = projectRoute({
        name: this.$route.name,
        hash: `#${tab}`,
        query: { ...this.$route.query }
      })

      this.$router.replace(route)
    },
    saveCampaignAsDraft() {
      this.isLoading = true
      RestApi(this.saveAsDraftEndpoint)
        .then(() => {
          this.$message.success(this.$t('CampaignForm_Saved'))
        })
        .catch(error => {
          this.$message.error(error.message)
        })
        .finally(() => {
          this.isLoading = false
          this.$router.push(projectRoute({ name: 'GameCampaignsList' }))
        })
    },
    launchCampaign() {
      this.isLoading = true
      RestApi(this.launchCampaignEndpoint)
        .then(({ data }) => {
          this.model.campaign.state = data['data']['state']
          this.$message.success(
            this.$t('Gamification_GameStepPreview_CampaignLaunched')
          )

          const routeName = this.$route.name
          if (routeName === 'GameCreateCampaignPage') {
            const route = projectRoute({
              name: 'GameEditCampaignPage',
              params: { campaignId: data['data']['id'] },
              hash: `#preview_launch`,
              query: { ...this.$route.query }
            })

            this.$router.replace(route)
          }
          EventBus.$emit('updateCampaign', {
            state: this.model.campaign.state,
            name: this.model.campaign.name
          })
        })
        .catch(error => {
          this.$message.error(error.message)
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    async copyCampaignUrl() {
      const url = this.model.campaign && this.model.campaign.frontend_url
      if (!url) return

      try {
        await navigator.clipboard.writeText(url)
        this.$message &&
          this.$message.success(this.$t('Gamification_CopyLink_Success'))
      } catch (e) {
        this.$message &&
          this.$message.error(this.$t('Gamification_CopyLink_Error'))
        console.error(e)
      }
    }
  }
}
</script>
<style scoped>
.steps-preview-wrapper {
  display: flex;
  flex-direction: column;
}
.content-step-wrapper {
  display: flex;
  flex-direction: column;
  height: 72px;
  justify-content: space-between;
}
.steps-preview-text {
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  line-height: 24px;
  color: #4b515f;
}
.steps-preview-text .link {
  color: #1d87c9;
  cursor: pointer;
  text-decoration: underline;
}
.margin-left-auto {
  margin-left: auto;
}
.buttons-wrapper {
  display: flex;
  align-items: center;
  gap: 40px;
  margin-top: 19px;
}

/deep/ .el-checkbox__inner:hover {
  border-color: #1d87c8;
}
/deep/ .is-checked .el-checkbox__inner {
  background-color: #1d87c8;
  border-color: #1d87c8;
}
/deep/ .el-checkbox {
  display: flex;
}
/deep/ .el-checkbox__label {
  order: -1;
  margin-right: 10px;
  line-height: 14px;
  color: #4b515f;
}

.key-value-wrapper {
  display: flex;
  flex-direction: column;
  gap: 30px;
}
.key-value-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  width: 100%;
}
.key-value-row .key {
  font-weight: 600;
}
.campaign-launch-section {
  border: 1px solid #dfe7ef;
  border-radius: 6px;
  box-shadow: 4px 4px 18px 0px #0000001a;
  padding: 15px 25px;
  display: flex;
  flex-direction: column;
  gap: 29px;
}
.campaign-launch-description {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.campaign-launch-description h3 {
  font-family: 'Lexend', sans-serif;
  font-size: 24px;
  font-weight: 600;
  line-height: 100%;
  color: #4b515f;
}
.campaign-launch-description p {
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  line-height: 100%;
  color: #4b515f;
}
.campaign-launch-page {
  padding: 0 20px 20px 0;
  background: #ffffff;
}
.campaign-launch-title-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
}
.verified-icon {
  font-size: 20px;
  color: #059669;
}
.campaign-launch-link-wrapper {
  background: #f5f9ff;
  padding: 15px 25px;
  border-radius: 6px;
}
.campaign-launch-link-wrapper .title {
  margin-bottom: 6px;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  color: #4b515f;
}
.link-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.link-container .link {
  color: #1e40af;
  font-size: 16px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.link-container .link:hover {
  text-decoration: underline;
  cursor: pointer;
}
.clone-icon {
  color: #4b515f;
  font-size: 18px;
  cursor: pointer;
  flex-shrink: 0;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/components/games-campaigns/preview/common/GameLaunchSubStep.vue